import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from "stylis";
import { create } from 'jss';
import rtl from 'jss-rtl';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { StylesProvider, jssPreset } from '@mui/styles';
import { getCookie, setCookie } from "../../../helpers/utils";

//theme
import { MuiPaperOverrides } from "./general/paperOverrides";

//context
import { ThemeContext } from "./context";

//constants
import { cookieTheme } from '../../../constants';
import { RTL_LANGUAGES } from '../../../constants/languages';

//store
import { useConfiguration } from "../../../store/common/configuration/useConfiguration";
import { selectBrandingData } from "../../../store/common/configuration/selectors";

interface IMuiThemeProviderProps {
  children: React.ReactNode,
}

const MuiThemeProvider: React.FC<IMuiThemeProviderProps> = ({
  children,
}): JSX.Element => {
  const { i18n } = useTranslation();
  const isRTL = RTL_LANGUAGES.includes(i18n.language);

  const { getBranding } = useConfiguration();

  const brandingData = useSelector(selectBrandingData);

  const cookieValue = getCookie(cookieTheme);

  const [mode, setMode] = useState(
    cookieValue && cookieValue !== 'null'
      ? cookieValue
      : 'light'
  );

  useEffect(() => {
    getBranding();
  }, []);

  useEffect(() => {

    if (getCookie(cookieTheme) === 'null' && brandingData?.themes) {
      const defaultTheme = brandingData.themes.find((theme: any) => theme.isDefault);
      if (defaultTheme) {
        const isMode = defaultTheme.mode;
        setMode(isMode);
      }
    }
  }, [brandingData]);

  const brandingPalette = useMemo(() => {
    if (!brandingData?.themes) return null;

    const currentTheme = brandingData.themes.find((theme: any) => theme.mode === mode)
      || brandingData.themes.find((theme: any) => theme.isDefault)
      || brandingData.themes[0];
    return currentTheme?.palette ?? null;
  }, [brandingData, mode]);

  const cacheRtl = useMemo(() => createCache({
    key: isRTL ? 'muirtl' : 'muiltr',
    stylisPlugins: isRTL ? [prefixer, rtlPlugin] : [],
    prepend: true,
  }), [isRTL]);

  const jss = useMemo(() => {
    const plugins = [...jssPreset().plugins];
    if (isRTL) {
      plugins.push(rtl());
    }
    return create({ plugins });
  }, [isRTL]);

  useEffect(() => {
    document.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
  }, [isRTL]);

  const handleChangeTheme = (value: boolean) => {
    const themeMode = value ? `dark` : `light`;

    const expiresDate = new Date();
    expiresDate.setDate(expiresDate.getDate() + 365);

    setMode(themeMode);
    setCookie(cookieTheme, themeMode, {
      expires: expiresDate,
      secure: true,
    })
  }
  
  const directionTheme = useMemo(() => {
    const basePalette = brandingPalette ?? {};

    return createTheme({
      ...basePalette,
      direction: isRTL ? 'rtl' : 'ltr',
      components: {
        ...(basePalette.components ?? {}),
        MuiPaper: {
          ...(basePalette.components?.MuiPaper ?? {}),
          styleOverrides: {
            ...(basePalette.components?.MuiPaper?.styleOverrides ?? {}),
            ...MuiPaperOverrides.styleOverrides,
          },
        },
      },
    });
  }, [brandingPalette, isRTL]);

  if (!brandingPalette) {
    return <></>;
  }

  return (
    <CacheProvider value={cacheRtl}>
      <StylesProvider jss={jss}>
        <ThemeContext.Provider value={{
          darkMode: mode === 'dark',
          setDarkMode: handleChangeTheme,
        }}>
          <ThemeProvider theme={directionTheme}>
            {children}
          </ThemeProvider>
        </ThemeContext.Provider>
      </StylesProvider>
    </CacheProvider>
  );
};

export default MuiThemeProvider;